import { useCallback } from 'react'

import { Editor } from '@tiptap/react'
import { nanoid } from 'nanoid'

import { useNetwork, useFileUploader } from '@tribeplatform/react-sdk/hooks'

import { fileSizeLimitError } from '../../common/lib/upload.js'
import { useStaticIntl } from '../../i18n/hooks/useStaticIntl.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { I18nShape } from '../../i18n/types.js'
import { useUploadQueue } from '../../Providers/UploadQueueProvider.js'
import {
  FileWithID,
  ComposerContextType,
  HandleAttachmentSelect,
} from '../@types/index.js'
import { findNode } from '../helpers/node.js'
import { composerError, fileUploadError } from '../utils/errors.js'

import { useAddImageToEditor } from './useAddImageToEditor.js'

type useHandleFilesProps = {
  editor: Editor
  handleAttachmentSelect: HandleAttachmentSelect
}

const composerFileSizeLimitError =
  (intl: I18nShape) => (limit: number | undefined) => {
    composerError(intl)(fileSizeLimitError(limit, 'file', intl))
  }

export const useHandleFiles = ({
  editor,
  handleAttachmentSelect,
}: useHandleFilesProps) => {
  const intl = useI18n()
  const { data: network } = useNetwork()
  const { addImage } = useAddImageToEditor()
  const { addToUploadQueue, removeFromUploadQueue } = useUploadQueue()
  const { uploadFile } = useFileUploader()

  const uploadVideo = useCallback(
    async (file: FileWithID) => {
      const fileSrc = URL.createObjectURL(file)
      const { type } = file

      editor
        .chain()
        .focus()
        .setVideo({ id: file.id, src: fileSrc, loading: true, type })
        .setTextSelection(editor.state.selection.anchor) // to reset the selection
        .run()

      const { id, url } = await uploadFile(file)

      const { offset, node } = findNode(
        editor.state,
        node => node.attrs.id === file.id,
      )

      editor
        .chain()
        .setNodeSelection(offset)
        .focus()
        .setVideoAttributes({
          loading: false,
          id,
          src: url,
          type,
        })
        .setTextSelection(offset + node.nodeSize)
        .insertContent(' ')
        .run()
    },
    [editor, uploadFile],
  )

  const getSizeLimitError = useStaticIntl(composerFileSizeLimitError)

  const onFileUpload: ComposerContextType['onFileUpload'] = useCallback(
    async (filesList, options = {}) => {
      const { asAttachment = false, attrs } = options
      // If not image, do nothing
      if (!filesList?.length) {
        return null
      }

      const files = Array.from(filesList) as FileWithID[]
      await Promise.all(
        files.map(async file => {
          const fileId = nanoid()
          const uploadQueueItem = { id: fileId }
          addToUploadQueue(uploadQueueItem)

          try {
            const isImage = !asAttachment && file.type?.startsWith('image')
            const isVideo =
              !asAttachment &&
              (file.type.includes('audio') || file.type.includes('video'))

            if (isImage && network?.settings?.imageSizeLimit < file.size) {
              getSizeLimitError(network?.settings?.imageSizeLimit)
              return
            }
            if (!isImage && network?.settings?.fileSizeLimit < file.size) {
              getSizeLimitError(network?.settings?.fileSizeLimit)
              return
            }

            file.id = fileId

            if (isVideo) {
              await uploadVideo(file)
            } else if (isImage) {
              await addImage(editor.view, file, attrs)
            } else {
              await handleAttachmentSelect([file])
            }
          } catch (error) {
            fileUploadError(intl)({
              fileName: file.name,
              message: error?.response?.errors?.[0]?.message || error.message,
            })

            if (file.id) {
              const foundNode = findNode(
                editor.state,
                node => node.attrs.id === file.id,
              )

              if (foundNode) {
                const { offset } = foundNode
                // Delete the actual node (image/video tag) from the DOM
                editor.commands.deleteRange({
                  from: offset,
                  to: offset + 1,
                })
              }
            }
          } finally {
            removeFromUploadQueue(uploadQueueItem)
          }
        }),
      )
    },
    [
      addToUploadQueue,
      network?.settings?.imageSizeLimit,
      network?.settings?.fileSizeLimit,
      getSizeLimitError,
      uploadVideo,
      addImage,
      editor.view,
      editor.state,
      editor.commands,
      handleAttachmentSelect,
      intl,
      removeFromUploadQueue,
    ],
  )

  return {
    onFileUpload,
  }
}
